<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, email as emailRule } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useAuthStore } from '@/stores/useAuthStore'

const { t } = useI18n()
const { umsokn } = useUmsokn()
const authStore = useAuthStore()

const props = defineProps<{
  disable?: boolean
  subHeader?: string
}>()

const rules = {
  umsokn: {
    name: { required },
    ssn: { required },
    address: { required },
    postalCode: { required },
    email: { required, email: emailRule },
    phone: { required }
  }
}

const v$ = useVuelidate(rules, { umsokn })


function digitsOnly(val: string) {
  return (val || '').replace(/\D/g, '')
}

// ApplicationBase will call this
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}

defineExpose({ validate })
</script>

<template>
  <v-row v-if="umsokn">
    <v-col cols="12">
      <h2 class="form__header">
        {{ t('umsokn_umsaekjandi_header') }}
      </h2>
    </v-col>

    <v-col v-if="props.subHeader" cols="12">
      <p>{{ props.subHeader }}</p>
    </v-col>

    <!-- Name (disabled) -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.name"
        :label="t('umsokn_umsaekjandi_nafn')"
        variant="outlined"
        :disabled="true"
        :error="v$.umsokn.name.$error"
        :error-messages="v$.umsokn.name.$error ? [t('umsokn_umsaekjandi_nafn_error')] : []"
      />
    </v-col>

    <!-- SSN (disabled) -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.ssn"
        :label="t('umsokn_umsaekjandi_kennitala')"
        variant="outlined"
        :disabled="true"
        :error="v$.umsokn.ssn.$error"
        :error-messages="v$.umsokn.ssn.$error ? [t('umsokn_umsaekjandi_kennitala_error')] : []"
      />
    </v-col>

    <!-- Address -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.address"
        :label="t('umsokn_umsaekjandi_heimilisfang')"
        variant="outlined"
        :readonly="props.disable"
        :error="v$.umsokn.address.$error"
        :error-messages="v$.umsokn.address.$error ? [t('umsokn_umsaekjandi_heimilisfang_error')] : []"
      />
    </v-col>

    <!-- Postal Code -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.postalCode"
        :label="t('umsokn_umsaekjandi_postnumer')"
        variant="outlined"
        :readonly="props.disable"
        type="tel"
        inputmode="numeric"
        @update:model-value="v => (umsokn.postalCode = digitsOnly(v))"
        :error="v$.umsokn.postalCode.$error"
        :error-messages="v$.umsokn.postalCode.$error ? [t('umsokn_umsaekjandi_postnumer_error')] : []"
      />
    </v-col>

    <!-- Email -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.email"
        :label="t('umsokn_umsaekjandi_netfang')"
        variant="outlined"
        :readonly="props.disable"
        type="email"
        :error="v$.umsokn.email.$error"
        :error-messages="v$.umsokn.email.$error
          ? [
              !v$.umsokn.email.required ? t('umsokn_umsaekjandi_netfang_error') : t('umsokn_umsaekjandi_netfang_invalid')
            ]
          : []"
      />
    </v-col>

    <!-- Phone -->
    <v-col cols="12" md="6">
      <v-text-field
        v-model="umsokn.phone"
        :label="t('umsokn_umsaekjandi_simanumer')"
        variant="outlined"
        :readonly="props.disable"
        type="tel"
        inputmode="numeric"
        @update:model-value="v => (umsokn.phone = digitsOnly(v))"
        :error="v$.umsokn.phone.$error"
        :error-messages="v$.umsokn.phone.$error ? [t('umsokn_umsaekjandi_simanumer_error')] : []"
      />
    </v-col>

    <!-- (Tilgreind has no spouse-disability bits; those were Maka-specific) -->
  </v-row>
</template>

<style scoped>
.form__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}
</style>
